import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GameMode, type GameResultPlayerEvent } from '@klurigo/common'
import type { FC } from 'react'
import { useEffect, useMemo, useState } from 'react'

import {
  Badge,
  Confetti,
  getBadgePositionBackgroundColor,
  getCelebrationLevel,
  ScoreChip,
  StreakBadge,
  Typography,
} from '../../components'
import { getBadgePositionTextColor } from '../../components/Badge/badge-utils'
import { classNames } from '../../utils/helpers'
import { GamePage, PlayerGameFooter, PointsBehindIndicator } from '../common'

import { getPositionMessage } from './message.utils'
import styles from './PlayerResultState.module.scss'

export interface PlayerResultStateProps {
  event: GameResultPlayerEvent
}

const PlayerResultState: FC<PlayerResultStateProps> = ({ event }) => {
  const { mode } = event.game
  const { nickname, score, behind } = event.player
  const { correct, last: lastScore, total: totalScore, position } = score
  const partiallyCorrect =
    mode === GameMode.Classic && !correct && lastScore > 0
  const streak =
    mode === GameMode.Classic && 'streak' in score ? score.streak : undefined
  const { current: currentQuestion, total: totalQuestions } = event.pagination
  const [showPosition, setShowPosition] = useState(false)

  useEffect(() => {
    if (mode === GameMode.ZeroToOneHundred) {
      setShowPosition(true)
    } else {
      const timer = setTimeout(() => {
        setShowPosition(true)
      }, 8000)
      return () => clearTimeout(timer)
    }
  }, [mode])

  const message = useMemo(
    () =>
      getPositionMessage(
        position,
        correct
          ? 'correct'
          : partiallyCorrect
            ? 'partially-correct'
            : 'incorrect',
      ),
    [position, correct, partiallyCorrect],
  )

  const celebrationLevel = useMemo(
    () => getCelebrationLevel(position, streak, correct),
    [correct, streak, position],
  )

  return (
    <GamePage
      layout="contained"
      footer={
        <PlayerGameFooter
          currentQuestion={currentQuestion}
          totalQuestions={totalQuestions}
          nickname={nickname}
          totalScore={totalScore}
        />
      }>
      <div
        className={classNames(
          styles.contentContainer,
          showPosition ? styles.shrink : undefined,
        )}>
        {mode === GameMode.Classic && (
          <div
            className={classNames(
              styles.correctnessBatch,
              showPosition ? styles.slideOutLeft : undefined,
            )}>
            <Typography variant="title" align="center" color="inverse">
              {correct
                ? 'Correct'
                : partiallyCorrect
                  ? 'Partially correct'
                  : 'Incorrect'}
            </Typography>

            <Badge
              size="large"
              backgroundColor={
                correct ? 'green' : partiallyCorrect ? 'orange' : 'red'
              }
              celebration={correct ? celebrationLevel : 'none'}>
              <FontAwesomeIcon
                icon={correct || partiallyCorrect ? faCheck : faXmark}
              />
            </Badge>
          </div>
        )}

        <div
          className={classNames(
            styles.positionBatch,
            showPosition ? styles.slideInRight : styles.hidden,
          )}>
          <Badge
            size="large"
            backgroundColor={getBadgePositionBackgroundColor(position)}
            textColor={getBadgePositionTextColor(position)}>
            {position}
          </Badge>
        </div>
      </div>

      {mode === GameMode.Classic && (
        <StreakBadge streak={streak}>Streak</StreakBadge>
      )}

      <ScoreChip value={lastScore} />

      <Typography variant="body" width="small" align="center" color="inverse">
        {message}
      </Typography>

      {behind && <PointsBehindIndicator {...behind} />}

      {celebrationLevel !== 'none' && (
        <Confetti trigger={true} intensity={celebrationLevel} />
      )}
    </GamePage>
  )
}

export default PlayerResultState
